export type StopType = 'merchant' | 'event';

export interface ItineraryStopRef {
  type: StopType;
  id: string;
  /** Event occurrence start (epoch ms). Distinguishes recurring CMS instances sharing the same id. */
  occurrenceAt?: number;
}

/** Stable key for selection, dedup, and trackBy. Events with occurrenceAt include the date. */
export function itineraryStopKey(ref: ItineraryStopRef): string {
  if (ref.type === 'event' && ref.occurrenceAt != null) {
    return `event:${ref.id}:${ref.occurrenceAt}`;
  }
  return `${ref.type}:${ref.id}`;
}

export function itineraryStopsEqual(a: ItineraryStopRef, b: ItineraryStopRef): boolean {
  return itineraryStopKey(a) === itineraryStopKey(b);
}

export function copyStopRef(ref: ItineraryStopRef): ItineraryStopRef {
  const copy: ItineraryStopRef = { type: ref.type, id: ref.id };
  if (ref.type === 'event' && ref.occurrenceAt != null) {
    copy.occurrenceAt = ref.occurrenceAt;
  }
  return copy;
}

export function eventOccurrenceAt(event: { event_date?: Date | string | null }): number | undefined {
  if (!event?.event_date) {
    return undefined;
  }
  return new Date(event.event_date).getTime();
}

export function eventStopRef(event: { id: string; event_date?: Date | string | null }): ItineraryStopRef {
  const ref: ItineraryStopRef = { type: 'event', id: event.id };
  const at = eventOccurrenceAt(event);
  if (at != null) {
    ref.occurrenceAt = at;
  }
  return ref;
}

export interface ItineraryStop extends ItineraryStopRef {
  addedAt: number;
  note?: string;
}

export interface PersonaList {
  id: string;
  heroImage?: string;
  tags?: string[];
  stops: ItineraryStopRef[];
}

export interface ItineraryState {
  name?: string;
  stops: ItineraryStop[];
  sourceList?: string | null;
  updatedAt: number;
}

export interface ItineraryRecord extends ItineraryState {
  id: string;
  createdAt: number;
  saved: boolean;
}

export const MAX_ITINERARIES = 3;

export const EMPTY_ITINERARY: ItineraryState = {
  name: undefined,
  stops: [],
  sourceList: null,
  updatedAt: 0
};

export function itineraryUid(): string {
  const g: any = typeof globalThis !== 'undefined' ? globalThis : (typeof window !== 'undefined' ? window : {});
  if (g && g.crypto && typeof g.crypto.randomUUID === 'function') {
    return g.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}
